import { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import SectionEditLink from './SectionEditLink';
import client from '../../../lib/feathers';

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_ORDER = [6, 0, 1, 2, 3, 4, 5]; // Saturday first, matching edit form

function formatTime(timeStr) {
  if (!timeStr) return '';
  if (timeStr === 'open_24_hours') return 'Open 24 hours';
  const parts = timeStr.split(' ');
  if (parts.length !== 2) return timeStr;
  let hour = parseInt(parts[0], 10);
  const minute = parts[1].padStart(2, '0');
  const ampm = hour >= 12 ? 'PM' : 'AM';
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${hour}:${minute} ${ampm}`;
}

function formatHourRange(hourObj) {
  if (!hourObj) return '';
  if (hourObj.opens === 'open_24_hours' || hourObj.closes === 'open_24_hours') {
    return 'Open 24 hours';
  }
  const opens = formatTime(hourObj.opens);
  const closes = formatTime(hourObj.closes);
  if (!opens && !closes) return '';
  return `${opens} - ${closes}`;
}

const STATUS_STYLES = {
  'Normal': { bg: 'bg-green-100', text: 'text-green-800', label: 'Operating Normally' },
  'By Appointment Only': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'By Appointment Only' },
  'Temporarily Closed': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Temporarily Closed' },
  'Permanently Closed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Permanently Closed' },
};

export default function HoursInfoPanel() {
  const listing = useSelector((s) => s.listings.selected);

  // For hosted listings, only show status if not Normal (i.e., closed)
  if (listing.hostListingId) {
    const hostedHours = listing.hours || {};
    const hostedStatus = hostedHours.status || { value: 'Normal' };
    if (hostedStatus.value === 'Normal') return null;
    const hostedStyle = STATUS_STYLES[hostedStatus.value] || STATUS_STYLES['Normal'];
    return (
      <div className="bg-white shadow overflow-hidden px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="px-4 py-5 sm:px-6 flex items-start justify-between gap-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Business Status
          </h3>
          <SectionEditLink section="hours" />
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${hostedStyle.bg} ${hostedStyle.text}`}>
                  {hostedStyle.label}
                </span>
              </dd>
            </div>
            {hostedStatus.message && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Status Message</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {hostedStatus.message}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    );
  }

  const hours = listing.hours || {};
  const status = hours.status || { value: 'Normal' };
  const days = hours.days || [];
  const statusStyle = STATUS_STYLES[status.value] || STATUS_STYLES['Normal'];

  return (
    <div className="bg-white shadow overflow-hidden px-4 py-5 sm:rounded-lg sm:p-6">
      <div className="px-4 py-5 sm:px-6 flex items-start justify-between gap-4">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Business Hours
        </h3>
        <SectionEditLink section="hours" />
      </div>

      <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
        <dl className="sm:divide-y sm:divide-gray-200">
          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                {statusStyle.label}
              </span>
            </dd>
          </div>
          {status.value !== 'Normal' && status.message && (
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Status Message</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {status.message}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {status.value === 'Normal' && days.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            {DAYS_ORDER.map(dayIndex => {
              const day = days[dayIndex];
              if (!day) {
                return (
                  <div key={dayIndex} className="py-3 sm:py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">{DAY_LABELS[dayIndex]}</dt>
                    <dd className="mt-1 text-sm text-gray-400 sm:mt-0 sm:col-span-2">Not set</dd>
                  </div>
                );
              }
              const isClosed = day.closed;
              const hourRanges = (day.hours || []).filter(h => h.opens || h.closes);
              return (
                <div key={dayIndex} className="py-3 sm:py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">{DAY_LABELS[dayIndex]}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {isClosed ? (
                      <span className="text-gray-400">Closed</span>
                    ) : hourRanges.length > 0 ? (
                      <div className="space-y-1">
                        {hourRanges.map((h, i) => (
                          <div key={i}>{formatHourRange(h)}</div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">Not set</span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      )}

      <HolidayHours listingId={listing?.id} />
    </div>
  );
}

const PREFERENCE_LABEL = {
  closed: 'Closed',
  normal_hours: 'Open, normal hours',
  special_hours: 'Special hours',
};

/**
 * Holidays a winery has said something about.
 *
 * The panel never showed these even though the data and the services have
 * existed all along — so a winery could set their Thanksgiving hours and then
 * never see them again anywhere except the edit form.
 *
 * Only renders when there is something to say; a listing with no holiday
 * preferences gets no empty box.
 */
function HolidayHours({ listingId }) {
  const user = useSelector((s) => s.auth.user);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!listingId || !user?.role) return;
      try {
        const res = await client.service(`${user.role}/listing-holiday-hours`).find({
          query: { listingId, $limit: 100 },
        });
        if (!cancelled) setRows(res.data || res || []);
      } catch (e) {
        console.log('holiday hours unavailable', e);
      }
    })();
    return () => { cancelled = true; };
  }, [listingId, user?.role]);

  const sorted = [...rows].sort((a, b) => {
    const da = a.holiday?.date || '';
    const db = b.holiday?.date || '';
    return da.localeCompare(db);
  });

  return (
    <>
      <div className="px-4 pt-6 pb-2 sm:px-6 flex items-start justify-between gap-4">
        <h4 className="text-base font-medium text-gray-900">Holidays</h4>
        <SectionEditLink section="holidays" />
      </div>
      <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
        {/* Rendered even when empty, or a winery with no holiday preferences
            would have nowhere to set their first one. */}
        {sorted.length === 0 && (
          <div className="py-4 sm:py-5 sm:px-6 text-sm text-gray-400">
            Nothing set for the holidays coming up.
          </div>
        )}
        <dl className="sm:divide-y sm:divide-gray-200">
          {sorted.map((row) => (
            <div key={row.id} className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                {row.holiday?.name || 'Holiday'}
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {PREFERENCE_LABEL[row.preference] || row.preference}
                {row.reason && (
                  <span className="block text-gray-500">{row.reason}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </>
  );
}
